export interface LayoutCircle {
  x: number;
  y: number;
  radius: number;
}

export interface LayoutPosition {
  x: number;
  y: number;
}

export interface LayoutBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface LayoutLabel {
  x: number;
  y: number;
  anchor: 'start' | 'end';
}

const CIRCLE_GAP = 2;
const LABEL_COLUMN_WIDTH = 140;

export function groupOverlapping(circles: LayoutCircle[]): number[][] {
  return circles.reduce<number[][]>((groups, circle, index) => {
    const touching = groups.filter((group) =>
      group.some((member) => overlaps(circles[member], circle)),
    );
    const untouched = groups.filter((group) => !touching.includes(group));
    return [...untouched, [...touching.flat(), index]];
  }, []);
}

export function spreadOverlapping(circles: LayoutCircle[], bounds: LayoutBounds): LayoutPosition[] {
  const spread = new Map(
    groupOverlapping(circles)
      .filter((group) => group.length > 1)
      .flatMap((group) => spreadGroup(circles, group, bounds)),
  );
  return circles.map((circle, index) => spread.get(index) ?? {x: circle.x, y: circle.y});
}

export function separateLabels(labels: LayoutLabel[], lineHeight: number): number[] {
  const order = labels
    .map((_, index) => index)
    .sort((first, second) => labels[first].y - labels[second].y);
  const placed = order.reduce<Map<number, number>>((positions, index) => {
    const label = labels[index];
    const lowestNeighbour = [...positions]
      .filter(([other]) => collides(labels[other], label))
      .map(([, y]) => y)
      .reduce((lowest, y) => Math.max(lowest, y), Number.NEGATIVE_INFINITY);
    positions.set(index, Math.max(label.y, lowestNeighbour + lineHeight));
    return positions;
  }, new Map());
  return labels.map((label, index) => placed.get(index) ?? label.y);
}

function spreadGroup(
  circles: LayoutCircle[],
  group: number[],
  bounds: LayoutBounds,
): [number, LayoutPosition][] {
  const members = [...group].sort((first, second) => circles[first].y - circles[second].y);
  const centerX = average(members.map((member) => circles[member].x));
  const centerY = average(members.map((member) => circles[member].y));
  const largestRadius = Math.max(...members.map((member) => circles[member].radius));
  const ringRadius = (largestRadius + CIRCLE_GAP) / Math.sin(Math.PI / members.length);
  return members.map((member, position) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * position) / members.length;
    const radius = circles[member].radius;
    return [
      member,
      {
        x: clamp(
          centerX + ringRadius * Math.cos(angle),
          bounds.left + radius,
          bounds.right - radius,
        ),
        y: clamp(
          centerY + ringRadius * Math.sin(angle),
          bounds.top + radius,
          bounds.bottom - radius,
        ),
      },
    ];
  });
}

function overlaps(first: LayoutCircle, second: LayoutCircle): boolean {
  return Math.hypot(first.x - second.x, first.y - second.y) < first.radius + second.radius;
}

function collides(first: LayoutLabel, second: LayoutLabel): boolean {
  return first.anchor === second.anchor && Math.abs(first.x - second.x) < LABEL_COLUMN_WIDTH;
}

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
